import type { RootState } from '@reducers/index';
import { updateGeo } from '@reducers/search';
import { getZones } from '@sbt-web/network/client';
import { GeoZone, GeoTown, GeoEntry, FilterURI } from '@sbt-web/network/types';

import {
  FormGroup,
  FormLabel,
  SelectBoxSkeleton,
  SelectOption,
  SelectOptionOptionType,
  DeviceWidth,
  SelectBox,
} from '@sbt-web/ui';
import React from 'react';
import { connect } from 'react-redux';

type ZoneOption = SelectOptionOptionType & {
  zone: GeoZone;
};

interface GZState {
  zones: ZoneOption[];
  loading: boolean;
}

interface GZStateProps {
  geo: GeoEntry;
}

interface GZDispatchProps {
  updateGeo: (geo: GeoEntry) => void;
}

interface OwnProps {
  trackSearchConversion: () => void;
}

type GZProps = GZStateProps & GZDispatchProps & OwnProps;

export class GeoZoneComponent extends React.Component<GZProps, GZState> {
  state: GZState = {
    zones: [],
    loading: false,
  };

  private async fetchZones() {
    const { town } = this.props.geo;
    if (!town || !town.hasZone) {
      this.setState({ zones: [], loading: false });
      return;
    }
    this.setState({ loading: true });
    try {
      const resp = await getZones(process.env.NEXT_PUBLIC_HADES_BASE_URL, town);

      const zones = resp.body.zones.map((zone) => ({
        zone,
        name: zone.value.replace(/\//g, ', '),
        value: zone.id,
      }));

      this.setState({ zones, loading: false });
    } catch {
      this.setState({ zones: [], loading: false });
    }
  }

  componentDidUpdate(prevProps: GZProps) {
    const { town: prevTown } = prevProps.geo;
    const { town: nextTown } = this.props.geo;

    if (!this.isChanged(prevTown, nextTown)) return;

    this.fetchZones();
  }

  private isChanged(
    prev: GeoTown | GeoZone | undefined,
    next: GeoTown | GeoZone | undefined
  ): boolean {
    return Boolean(
      (prev && !next) ||
        (!prev && next) ||
        (prev && next && prev.id !== next.id)
    );
  }

  componentDidMount() {
    this.fetchZones();
  }

  handleSelection = (selection: SelectOption[]) => {
    const { zones } = this.state;
    const selected = selection[0];
    let zone: GeoZone | undefined = undefined;

    if (selected) {
      const found = zones.find((z) => z.value === selected.value);
      zone = found ? found.zone : undefined;
    }

    this.props.trackSearchConversion();
    this.props.updateGeo({ ...this.props.geo, zone });
  };

  render() {
    const { zones, loading } = this.state;
    const { geo } = this.props;

    if (zones.length === 0 && !loading) return null;

    const selectedIndex = zones.findIndex(
      (z) => geo.zone && z.value === geo.zone.id
    );

    // Refactor to use the viewport hook when this is a function
    const flavor = window.matchMedia(`(max-width: ${DeviceWidth.mobile.max})`)
      .matches
      ? 'large'
      : 'small';
    return (
      <FormGroup>
        <FormLabel>Zona</FormLabel>
        {loading ? (
          <SelectBoxSkeleton expanded={false} flavor={flavor} />
        ) : (
          <SelectBox
            name={FilterURI.GeoZone}
            flavor={flavor}
            nullable
            selectedIndices={selectedIndex >= 0 ? [selectedIndex] : []}
            placeholder={'Qualsiasi'}
            valueList={zones}
            onSelect={this.handleSelection}
          />
        )}
      </FormGroup>
    );
  }
}

export const GeoZoneSelector = connect<
  GZStateProps,
  GZDispatchProps,
  OwnProps,
  RootState
>(
  (state, ownProps) => ({
    geo: state.search.geo,
    trackSearchConversion: ownProps.trackSearchConversion,
  }),
  (dispatch) => ({
    updateGeo: (geo: GeoEntry) => dispatch(updateGeo(geo)),
  })
)(GeoZoneComponent);
